import { buildEventPayload, isMarkable } from 'common/instrumentation';
import queueEvents from 'communicator/internal/queue-events';
import { MarkOptionsArg, Timing } from 'types/instrumentation';

const timings: Timing = {};

export const queueLogEntry = (
  eventName: string,
  stime: number,
  etime: number,
  options?: MarkOptionsArg
) => {
  options = options || { doFlush: false, failure: false, manualTiming: false, reason: undefined };
  const startKey = `${eventName}_start`;
  const endKey = `${eventName}_end`;
  timings[startKey] = stime;
  timings[endKey] = etime;

  const payload = buildEventPayload({
    key: eventName,
    options,
    timings
  });

  if (payload) {
    queueEvents({
      source: window,
      origin: window.location.origin,
      data: payload
    });
  }
};

export const logRecognizeNoVbaToken = () => {
  queueLogEntry('recognize_no_vba_token', 1, 1, {
    doFlush: false,
    manualTiming: true
  });
};

export const logVbaSlaFailure = () => {
  queueLogEntry('vba_sla_failure', 1, 10001, {
    doFlush: true,
    manualTiming: true
  });
};

export const markStart = (
  key: string,
  options: MarkOptionsArg = {
    doFlush: false,
    failure: false,
    onlyOnce: false,
    reason: undefined
  }
) => {
  if (isMarkable) {
    const startKey = `${key}_start`;

    if (options.onlyOnce && performance.getEntriesByName(startKey).length > 0) {
      return;
    }

    performance.mark(startKey);
    timings[startKey] = Date.now();
  }
};

export const markEnd = (
  key: string,
  options: MarkOptionsArg = {
    doFlush: false,
    failure: false,
    onlyOnce: false,
    reason: undefined
  }
) => {
  if (isMarkable) {
    const startKey = `${key}_start`;
    const endKey = `${key}_end`;

    if (options.onlyOnce && performance.getEntriesByName(startKey).length > 0) {
      return;
    }

    performance.mark(endKey);
    timings[endKey] = Date.now();
    queueLogEntry(key, timings[startKey], timings[endKey], options);
  }
};

// RCA: performance API returns unusual duration value
/* istanbul ignore next */
const isSaneDuration = (n: number): boolean => {
  return n > 2 && n < 60000; // prevent garbage from polluting logging.
};

/**
 * Special case to be inclusive of downstream deps (visa-sdk, iframe, thm, vba)
 */
export const initMeasure = (stime: EpochTimeStamp, etime: EpochTimeStamp) => {
  if (!isMarkable) {
    return;
  }

  // EMVCo init() measurement complete
  queueLogEntry('INIT_COMPLETE', stime, Math.floor(etime));

  // setTimeout to allow delta from visaSdk init() complete to communicator+downstream to load completely
  /* istanbul ignore next */
  setTimeout(() => {
    // communicator iframe timing (doc load only, iframe.duration include blocking asset loads)
    const iframe = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    queueLogEntry('init_communicator_iframe', iframe.fetchStart, iframe.responseEnd, {
      manualTiming: true
    });

    // communicator iframe, static assets, THM, VBA, etc.
    const hostpath = `${window.location.origin}/checkout-widget`;
    const isCommunicatorJs = new RegExp(`${hostpath}/resources/src-system/communicator.*.js`, 'i');
    const isVbaJs = /\/resources\/vba\/js\/vba.*.js$/i;
    const isThm = RegExp('https://thm.visa.com/', 'i');
    let thmLoad = 0;

    const resources = performance.getEntriesByType('resource') as Array<PerformanceResourceTiming>;
    resources.forEach((timing: PerformanceResourceTiming) => {
      if (['script', 'iframe'].indexOf(timing.initiatorType) > -1) {
        // cached assets *may* report duration as unix TS at moment of read from disk
        const saneDuration = isSaneDuration(timing.duration) ? timing.duration : 1;

        if (isCommunicatorJs.test(timing.name)) {
          queueLogEntry('init_communicator_js_load', 1, saneDuration, { manualTiming: true });
        } else if (isVbaJs.test(timing.name)) {
          queueLogEntry('init_vba_js_load', 1, saneDuration, { manualTiming: true });
        } else if (isThm.test(timing.name)) {
          thmLoad += saneDuration;
        }
      }
    });

    if (thmLoad) {
      queueLogEntry('thm_init', 1, thmLoad, { doFlush: true, manualTiming: true });
    }
  }, 10000);
};
